
/**
 * One way to call the API from the browser.
 *
 * There were twenty-four fetch call sites and five different shapes of error
 * handling between them. The practical cost was that a 422 from zod — which
 * carries a `details` array naming the field and what is wrong with it —
 * reached the user from exactly one of them. Everywhere else they got
 * "Validation failed", or "Could not save", or nothing.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The zod issues, when the failure was a validation one. */
    readonly details?: { path?: PropertyKey[]; message?: string }[],
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type Payload = {
  error?: string
  details?: { path?: PropertyKey[]; message?: string }[]
}

/**
 * The message a person should read. A validation failure names the fields;
 * anything else falls back to the server's message, then to the status.
 */
function messageFrom(response: Response, payload: Payload): string {
  const issues = payload.details?.map((issue) => issue.message).filter(Boolean) as string[] | undefined
  if (issues?.length) return issues.join(' ')
  if (payload.error) return payload.error
  if (response.status === 403) return 'You do not have permission to do that.'
  if (response.status === 404) return 'That is no longer there. Reload and try again.'
  if (response.status >= 500) return 'Something went wrong on our side. Try again.'
  return response.statusText || 'Request failed'
}

/**
 * Calls the API and returns the parsed body, or throws an ApiError carrying a
 * message worth showing someone.
 */
export async function apiFetch<T = unknown>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers:
      init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json', ...init?.headers }
        : init?.headers,
  })

  // A 204, or a body that is not JSON at all: neither is a reason to throw a
  // parser error at someone.
  let payload: Payload = {}
  if (response.status !== 204) {
    payload = await response.json().catch(() => ({}) as Payload)
  }

  if (!response.ok) {
    throw new ApiError(messageFrom(response, payload), response.status, payload.details)
  }
  return payload as T
}

/** The message from any thrown value, for a catch block that has to show something. */
export function errorMessage(cause: unknown, fallback = 'Something went wrong'): string {
  if (cause instanceof ApiError) return cause.message
  if (cause instanceof Error) return cause.message
  return fallback
}
